import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class ReportableTransactionService {

  constructor(private http: HttpClient) { }

  /**
   * Get Reportable Transaction Types
   * Action Code: 'w1ubsf'
   */
  getReportableTransactionTypes(
    baseUrl: string,
    rowData: any,
    ssoId: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    const jcdKey = rowData.JCD_KEY || globalParams?.jcd_key || 250;
    const taxYear = rowData.TAX_YEAR || globalParams?.tax_year || filterParams?.tax_year;
    const scenario = rowData.SCENARIO_KEY || globalParams?.scenario || filterParams?.scenario;
    
    // Merge globalParams and filterParams first, then override with specific params
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'w1ubsf';
    params.sso_id = ssoId;
    params.tax_year = taxYear;
    params.scenario = scenario;
    params.jcd_key = jcdKey;
    
    console.log('getReportableTransactionTypes params:', params);
    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Reportable Transaction Reason Types
   * Action Code: 'hg67p4'
   */
  getReportableTransReasonTypes(
    baseUrl: string,
    rowData: any,
    ssoId: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    const jcdKey = rowData.JCD_KEY || globalParams?.jcd_key || 250;
    const taxYear = rowData.TAX_YEAR || globalParams?.tax_year || filterParams?.tax_year;
    const scenario = rowData.SCENARIO_KEY || globalParams?.scenario || filterParams?.scenario;
    
    // Merge globalParams and filterParams first, then override with specific params
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'hg67p4';
    params.sso_id = ssoId;
    params.tax_year = taxYear;
    params.scenario = scenario;
    params.jcd_key = jcdKey;
    
    console.log('getReportableTransReasonTypes params:', params);
    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Local Account List
   * Action Code: 'wj7wci'
   */
  getLocalAccountList(
    baseUrl: string,
    rowData: any,
    ssoId: string,
    repTransId: number,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    const jcdKey = rowData.JCD_KEY || globalParams?.jcd_key || 250;
    const taxYear = rowData.TAX_YEAR || globalParams?.tax_year || filterParams?.tax_year;
    const scenario = rowData.SCENARIO_KEY || globalParams?.scenario || filterParams?.scenario;
    
    // Merge globalParams and filterParams first, then override with specific params
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'wj7wci';
    params.sso_id = ssoId;
    params.tax_year = taxYear;
    params.scenario = scenario;
    params.jcd_key = jcdKey;
    params.flc_code = 'CGNI';
    params.chart_key = '500386317';
    params.combination_key = rowData.COMBINATION_KEY;
    params.rep_trans_id = repTransId;
    
    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Local Account Amount Data
   * Action Code: 'agsirv'
   */
  getLocalAccountAmtData(
    baseUrl: string,
    rowData: any,
    ssoId: string,
    accountKey: number,
    repTransId: number,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    const jcdKey = rowData.JCD_KEY || globalParams?.jcd_key || 250;
    const taxYear = rowData.TAX_YEAR || globalParams?.tax_year || filterParams?.tax_year;
    const scenario = rowData.SCENARIO_KEY || globalParams?.scenario || filterParams?.scenario;
    
    // Merge globalParams and filterParams first, then override with specific params
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'agsirv';
    params.sso_id = ssoId;
    params.tax_year = taxYear;
    params.scenario = scenario;
    params.jcd_key = jcdKey;
    params.cc_key = rowData.COMBINATION_KEY;
    params.gl_acct_key = accountKey;
    params.trans_type = 'SCHDD_REPORTABLE_TRANS';
    params.rep_trans_id = repTransId;
    params.issue_key = rowData.ISSUE_KEY;
    
    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Prior Reportable Adjustment
   * Action Code: '194d7r'
   */
  getPriorReportableAdjustment(
    baseUrl: string,
    rowData: any,
    repTransId: number,
    ssoId: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    const jcdKey = rowData.JCD_KEY || globalParams?.jcd_key || 250;
    const taxYear = rowData.TAX_YEAR || globalParams?.tax_year || filterParams?.tax_year;
    const scenario = rowData.SCENARIO_KEY || globalParams?.scenario || filterParams?.scenario;
    
    // Merge globalParams and filterParams first, then override with specific params
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = '194d7r';
    params.sso_id = ssoId;
    params.tax_year = taxYear;
    params.scenario = scenario;
    params.jcd_key = jcdKey;
    params.combination_key = rowData.COMBINATION_KEY;
    params.rep_source = 'SCHDD_REPORTABLE_TRANS';
    params.rep_trans_id = repTransId;
    params.issue_key = rowData.ISSUE_KEY;
    
    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Rep Trans Header
   * Action Code: '5z0g9r'
   */
  getRepTransHeader(
    baseUrl: string,
    dataRow: any,
    rowData: any,
    repTransId: number,
    ssoId: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    const jcdKey = rowData.JCD_KEY || globalParams?.jcd_key || 250;
    const taxYear = rowData.TAX_YEAR || globalParams?.tax_year || filterParams?.tax_year;
    const scenario = rowData.SCENARIO_KEY || globalParams?.scenario || filterParams?.scenario;
    
    // Merge globalParams and filterParams first, then override with specific params
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = '5z0g9r';
    params.sso_id = ssoId;
    params.tax_year = taxYear;
    params.scenario = scenario;
    params.jcd_key = jcdKey;
    params.combination_key = rowData.COMBINATION_KEY;
    params.rep_source = 'SCHDD_REPORTABLE_TRANS';
    params.rep_trans_id = repTransId;
    params.schdd_detail_key = dataRow.Trans_details_key;
    params.schdd_occurrence = dataRow.Occurrence;
    params.issue_key = rowData.ISSUE_KEY;
    
    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Get Rep Trans Details
   * Action Code: 'bpp2tl'
   */
  getRepTransDetails(
    baseUrl: string,
    dataRow: any,
    rowData: any,
    repTransId: number,
    ssoId: string,
    globalParams?: any,
    filterParams?: any
  ): Observable<any> {
    const jcdKey = rowData.JCD_KEY || globalParams?.jcd_key || 250;
    const taxYear = rowData.TAX_YEAR || globalParams?.tax_year || filterParams?.tax_year;
    const scenario = rowData.SCENARIO_KEY || globalParams?.scenario || filterParams?.scenario;
    
    // Merge globalParams and filterParams first, then override with specific params
    let params: any = _.merge({}, globalParams || {}, filterParams || {});
    params.action_code = 'bpp2tl';
    params.sso_id = ssoId;
    params.tax_year = taxYear;
    params.scenario = scenario;
    params.jcd_key = jcdKey;
    params.combination_key = rowData.COMBINATION_KEY;
    params.rep_source = 'SCHDD_ADJ_REP_TRANS';
    params.rep_trans_id = repTransId;
    params.schdd_detail_key = dataRow.Trans_details_key;
    params.schdd_occurrence = dataRow.Occurrence;
    params.issue_key = rowData.ISSUE_KEY;
    
    return this._makeLoadRequest(baseUrl, params);
  }

  /**
   * Save Schedule D Reportable Transaction
   * Action Code: 'vlae0g'
   * 
   * AngularJS Pattern:
   * var params = filterParams;
   * params = _.merge({}, GlobalService.globalParams, params);
   * params = _.extend({json_data:json_data}, params);
   * params = _.extend({json_settings:json_settings}, params);
   * $http({method: "post", url: url, data: params})
   */
  saveSchddReportableTransaction(
    baseUrl: string,
    rowData: any,
    ssoId: string,
    repTransHeader: any,
    repTransDetails: any[],
    repTransId: number,
    screenKey: string,
    globalService: any,
    filterParams: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=vlae0g`;
    
    const repTransHeaderArr = [repTransHeader];
    const jsonData = JSON.stringify(repTransHeaderArr);
    
    console.log('=== Save Reportable Transaction Debug ===');
    console.log('repTransHeader:', repTransHeader);
    console.log('repTransDetails (before stringify):', repTransDetails);
    console.log('repTransDetails length:', repTransDetails?.length || 0);
    console.log('ADJ_SOURCE:', repTransHeader?.ADJ_SOURCE);
    
    const jsonSettings = JSON.stringify(repTransDetails);
    console.log('jsonSettings (stringified):', jsonSettings);
    
    // Extract globalParams - globalService might BE globalParams or have globalParams property
    const globalParams = globalService?.globalParams || globalService || {};
    
    console.log('globalService:', globalService);
    console.log('Extracted globalParams:', globalParams);
    console.log('filterParams:', filterParams);
    
    // Build params EXACTLY like AngularJS:
    // var params = filterParams;
    // params = _.merge({}, GlobalService.globalParams, params);
    let params: any = _.cloneDeep(filterParams) || {};
    params = _.merge({}, globalParams, params);
    
    // Extend with json_data and json_settings
    params = _.extend({ json_data: jsonData }, params);
    params = _.extend({ json_settings: jsonSettings }, params);
    
    params.process_name = 'Schedule-D-Reportable-Transaction-save';
    params.rep_trans_id = repTransId;
    params.screen_key = screenKey;
    params.combination_key = rowData.COMBINATION_KEY;
    
    // CRITICAL: These fields were MISSING and are required by the backend!
    // Compare with working AngularJS payload - these must be present
    
    // 1. scenarioCode - required for backend validation
    params.scenarioCode = filterParams?.scenarioCode || 
                          filterParams?.scenario_code || 
                          rowData?.SCENARIO_TYPE_CODE || 
                          globalParams?.scenarioCode ||
                          'RAF';
    
    // 2. is_issue_key_enabled - required for backend validation
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || 
                                   rowData?.is_issue_key_enabled || 
                                   globalParams?.is_issue_key_enabled ||
                                   'N';
    
    // 3. checkLockedbj - CRITICAL: build from rowData (matches AngularJS gridController.js structure)
    const checkLockedbj = [{
      leid: rowData.LEID || rowData.leid || '',
      me_code: rowData.ME_CODE || rowData.me_code || '',
      cdr_number: rowData.CDR_NO || rowData.cdr_number || '',
      consol_group_key: rowData.CONSOL_GROUP_KEY || rowData.consol_group_key || '1',
      reporting_period: rowData.REPORTING_PERIOD || rowData.reporting_period || '1',
      combination_key: rowData.COMBINATION_KEY || rowData.combination_key || '',
      scenario_key: String(filterParams?.scenario || globalParams?.scenario || rowData?.SCENARIO_KEY || '74')
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);
    
    // Ensure all required parameters are STRINGS
    params.tax_year = String(params.tax_year || filterParams?.tax_year || globalParams?.tax_year || rowData?.TAX_YEAR || '');
    params.scenario = String(params.scenario || filterParams?.scenario || globalParams?.scenario || rowData?.SCENARIO_KEY || '');
    params.jcd_key = String(params.jcd_key || filterParams?.jcd_key || globalParams?.jcd_key || rowData?.JCD_KEY || '250');
    
    console.log('Final params:', params);
    console.log('Required params - tax_year:', params.tax_year, '(type:', typeof params.tax_year, ')');
    console.log('Required params - scenario:', params.scenario, '(type:', typeof params.scenario, ')');
    console.log('Required params - jcd_key:', params.jcd_key, '(type:', typeof params.jcd_key, ')');
    console.log('scenarioCode:', params.scenarioCode);
    console.log('is_issue_key_enabled:', params.is_issue_key_enabled);
    console.log('checkLockedbj:', params.checkLockedbj);

    // Convert to URLSearchParams for form-urlencoded format (matching AngularJS transformRequest behavior)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });
    
    // Ensure critical fields are set
    urlEncoded.set('tax_year', params.tax_year);
    urlEncoded.set('scenario', params.scenario);
    urlEncoded.set('jcd_key', params.jcd_key);
    urlEncoded.set('scenarioCode', params.scenarioCode);
    urlEncoded.set('is_issue_key_enabled', params.is_issue_key_enabled);
    urlEncoded.set('checkLockedbj', params.checkLockedbj);
    
    console.log('URLSearchParams string:', urlEncoded.toString());

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Private helper method to make HTTP requests
   * Merges all params and sends as URL-encoded form data
   */
  private _makeLoadRequest(baseUrl: string, params: any): Observable<any> {
    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        urlParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${urlParams.toString()}`;
    
    console.log('API Request URL:', url);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, '', { headers, withCredentials: true });
  }
}
